import { stopActiveServoSession } from "../ik/ikTarget/servoSession";
import { getSceneState } from "../stores/sceneStore";
import { usePhysicsStore } from "../stores/physicsStore";
import { getPhysicsWorker } from "./workerHandle";
import {
  createPositionActuatorControlPayload,
  updateGlobalActuatorMapping,
} from "./actuatorMapping";
import {
  generateActuatorControlName,
  generateStableActuatorNameFromUuid,
} from "../utils/actuatorIds";
import { buildBodyRootMap, findBodyForJointUuid, type BodyMap } from "../utils/bodyHierarchy";
import { resolveJointUuidFromRef } from "../utils/jointUtils";
import type { Actuator, Body, BodyUUID, Joint, Tendon, WorldBody } from "../types/scene";

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
type ScalarJoint = Extract<Joint, { kind: "hinge" | "slide" }>;

/** Strip MJCF-style path prefix before `/` for UI labels (matches inspector `stripPrefix`). */
function stripNamePrefixForUi(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return trimmed;
  const slash = trimmed.lastIndexOf("/");
  return slash >= 0 ? trimmed.slice(slash + 1) : trimmed;
}

function actuatorDisplayLabelsForMatches(
  matches: Actuator[],
  jointDisplayFallback: string
): string {
  const labels = matches
    .map((a) => {
      const raw = typeof a?.name === "string" ? a.name.trim() : "";
      return raw ? stripNamePrefixForUi(raw) : "";
    })
    .filter(Boolean);
  const uniq = [...new Set(labels)].sort((a, b) => a.localeCompare(b));
  if (uniq.length > 0) return uniq.join(", ");
  return jointDisplayFallback;
}

/** Minimal scene slice needed for actuator UI keys / clamping. */
export type ActuatorMirrorScene = {
  joints?: Record<string, Joint>;
  actuators?: Record<string, Actuator>;
  bodies?: Record<string, Body>;
  worldBodies?: Array<WorldBody | BodyUUID>;
};

const isScalarJoint = (joint: Joint | undefined): joint is ScalarJoint =>
  !!joint && (joint.kind === "hinge" || joint.kind === "slide");

function isPositionLikeActuator(actuator: Actuator | null | undefined): boolean {
  const t = String(actuator?.kind || "").toLowerCase();
  return t === "position" || t === "intposition" || t === "general";
}

function actuatorsForJointDrag(
  jointUuid: string,
  jointName: string | undefined,
  actuatorList: Actuator[],
  joints: Record<string, Joint> = {}
): Actuator[] {
  return actuatorList.filter((act) => {
    if (!act?.joint) return false;
    if (!isPositionLikeActuator(act)) return false;
    const targetJoint = act.joint;
    return (
      targetJoint === jointUuid ||
      (!!jointName && targetJoint === jointName) ||
      resolveJointUuidFromRef(targetJoint, joints) === jointUuid
    );
  });
}

function actuatorTargetsJoint(
  actuator: Actuator,
  jointUuid: string,
  jointName: string | undefined,
  joints: Record<string, Joint>
): boolean {
  if (!actuator?.joint) return false;
  const targetJoint = actuator.joint;
  if (targetJoint === jointUuid || (!!jointName && targetJoint === jointName)) return true;
  return resolveJointUuidFromRef(targetJoint, joints) === jointUuid;
}

function resolveTendonForActuator(
  actuator: Actuator,
  tendons: Record<string, Tendon | undefined>
): Tendon | null {
  const tendonUuid = typeof actuator?.tendonUuid === "string" ? actuator.tendonUuid : "";
  if (tendonUuid) {
    return (
      tendons[tendonUuid] || Object.values(tendons).find((t) => t?.uuid === tendonUuid) || null
    );
  }

  const tendonName = typeof actuator?.tendon === "string" ? actuator.tendon : "";
  if (!tendonName) return null;
  return (
    tendons[tendonName] ||
    Object.values(tendons).find((t) => t?.uuid === tendonName || t?.name === tendonName) ||
    null
  );
}

function tendonHasJoint(
  tendon: Tendon | null,
  jointUuid: string,
  joints: Record<string, Joint>
): boolean {
  if (!tendon || !Array.isArray(tendon.joints)) return false;
  return tendon.joints.some((entry) => {
    const ref = entry?.joint;
    return ref === jointUuid || resolveJointUuidFromRef(ref, joints) === jointUuid;
  });
}

function getActuatorControlStateKey(
  actuator: Actuator,
  joints: Record<string, Joint>,
  bodies: Record<string, Body>,
  worldBodies: Array<WorldBody | BodyUUID>
): string | null {
  if (actuator?.uuid) {
    return generateStableActuatorNameFromUuid(String(actuator.uuid));
  }
  const jointUuid = resolveJointUuidFromRef(actuator.joint, joints);
  if (!jointUuid) return null;
  const bodyMap = bodies as BodyMap;
  const bodyUuid = findBodyForJointUuid(bodyMap, jointUuid);
  const bodyRootMap = buildBodyRootMap(bodyMap, worldBodies || []);
  const rootUuid = bodyUuid ? bodyRootMap[bodyUuid] || bodyUuid : null;
  const jointEntry = joints[jointUuid];
  const baseName =
    (typeof actuator?.name === "string" && actuator.name) ||
    (typeof jointEntry?.name === "string" && jointEntry.name) ||
    "actuator";
  return generateActuatorControlName({
    baseName,
    jointUuid,
    rootBodyUuid: rootUuid,
    tendonName: actuator?.tendon || null,
  });
}

/** Slider min/max for a joint-coupled position actuator (matches ActuatorControlsPanel). */
function sliderBoundsForJointActuator(
  actuator: Actuator,
  jointEntry: ScalarJoint
): { min: number; max: number } {
  if (actuator?.tendon) {
    const lo = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[0] : 0;
    const hi = Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange[1] : 255;
    const min = Number.isFinite(lo) ? lo : 0;
    const max = Number.isFinite(hi) ? hi : 255;
    return { min, max };
  }

  const ctrlLo =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[0]
      : null;
  const ctrlHi =
    Array.isArray(actuator?.ctrlrange) && actuator.ctrlrange.length === 2
      ? actuator.ctrlrange[1]
      : null;
  const hasCtrlRange =
    Number.isFinite(ctrlLo) && Number.isFinite(ctrlHi) && (ctrlHi as number) > (ctrlLo as number);

  const jointLo = jointEntry?.range?.[0];
  const jointHi = jointEntry?.range?.[1];
  const hasJointRange = Number.isFinite(jointLo) && Number.isFinite(jointHi) && jointHi > jointLo;

  const min =
    hasCtrlRange && hasJointRange
      ? Math.max(ctrlLo as number, jointLo)
      : hasCtrlRange
        ? (ctrlLo as number)
        : hasJointRange
          ? jointLo
          : -Math.PI;
  const max =
    hasCtrlRange && hasJointRange
      ? Math.min(ctrlHi as number, jointHi)
      : hasCtrlRange
        ? (ctrlHi as number)
        : hasJointRange
          ? jointHi
          : Math.PI;
  return { min, max };
}

/**
 * Build UI control keys → slider values for position-like actuators on this joint
 * (same rules as ActuatorControlsPanel).
 */
export function collectActuatorPositionUiUpdatesForJoint(
  scene: ActuatorMirrorScene,
  jointUuid: string,
  clampedJointTarget: number
): Record<string, number> {
  const joints = scene.joints || {};
  const joint = joints[jointUuid];
  if (!isScalarJoint(joint)) return {};

  const actuatorList = Object.values(scene.actuators || {}) as Actuator[];
  const matches = actuatorsForJointDrag(jointUuid, joint.name, actuatorList, joints);
  if (matches.length === 0) return {};

  const bodies = scene.bodies || {};
  const worldBodies = scene.worldBodies || [];
  const out: Record<string, number> = {};
  for (const act of matches) {
    const stateKey = getActuatorControlStateKey(act, joints, bodies, worldBodies);
    if (!stateKey) continue;
    const { min, max } = sliderBoundsForJointActuator(act, joint);
    const ui =
      Number.isFinite(min) && Number.isFinite(max) && max > min
        ? clamp(clampedJointTarget, min, max)
        : clampedJointTarget;
    out[stateKey] = ui;
  }
  return out;
}

/**
 * Keeps physicsStore `actuatorPositionTargets` aligned with scene joint targets while physics is running.
 * Does not post to the worker (callers that drive MuJoCo send controls separately).
 */
export function mirrorActuatorPositionTargetsForJointFromScene(
  scene: ActuatorMirrorScene,
  jointUuid: string,
  clampedJointTarget: number
): void {
  const physics = usePhysicsStore.getState();
  if (!physics.isRunning) return;

  const map = collectActuatorPositionUiUpdatesForJoint(scene, jointUuid, clampedJointTarget);
  for (const [key, value] of Object.entries(map)) {
    physics.actions.setActuatorPositionTarget(key, value);
  }
}

export function getJointPositionActuatorSliderState(
  jointUuid: string,
  actuatorPositionTargets: Record<string, number> = usePhysicsStore.getState()
    .actuatorPositionTargets
): {
  min: number;
  max: number;
  step: number;
  value: number;
  controlKey: string;
  actuatorDisplayLabel: string;
} | null {
  const scene = getSceneState();
  const joint = (scene?.joints as Record<string, Joint | undefined>)?.[jointUuid];
  if (!isScalarJoint(joint)) return null;

  const actuatorList = Object.values(scene.actuators || {}) as Actuator[];
  const matches = actuatorsForJointDrag(jointUuid, joint.name, actuatorList, scene.joints || {});
  if (matches.length === 0) return null;

  const jointNameRaw = typeof joint.name === "string" ? joint.name.trim() : "";
  const jointDisplayFallback = jointNameRaw ? stripNamePrefixForUi(jointNameRaw) : "";

  const bodies = scene.bodies || {};
  const worldBodies = scene.worldBodies || [];
  const actuator = matches[0];
  const controlKey = getActuatorControlStateKey(actuator, scene.joints || {}, bodies, worldBodies);
  if (!controlKey) return null;

  const { min, max } = sliderBoundsForJointActuator(actuator, joint);
  const hasRange = Number.isFinite(min) && Number.isFinite(max) && max > min;
  const storedValue = actuatorPositionTargets?.[controlKey];
  const fallbackValue = Number.isFinite(Number(joint.ref)) ? Number(joint.ref) : hasRange ? min : 0;
  const rawValue = Number.isFinite(Number(storedValue)) ? Number(storedValue) : fallbackValue;
  const value = hasRange ? clamp(rawValue, min, max) : rawValue;

  const actuatorDisplayLabel = actuatorDisplayLabelsForMatches(matches, jointDisplayFallback);

  return {
    min: hasRange ? min : -Math.PI,
    max: hasRange ? max : Math.PI,
    step: hasRange ? (max - min) / 200 || 0.01 : 0.01,
    value,
    controlKey,
    actuatorDisplayLabel,
  };
}

export function hasAssignedActuatorForJoint(jointUuid: string): boolean {
  const scene = getSceneState();
  const joints = scene?.joints || {};
  const joint = (joints as Record<string, Joint | undefined>)[jointUuid];
  if (!joint) return false;

  const actuatorList = Object.values(scene.actuators || {}) as Actuator[];
  return actuatorList.some((actuator) =>
    actuatorTargetsJoint(actuator, jointUuid, joint.name, joints)
  );
}

export function hasPositionLikeActuatorForJoint(jointUuid: string): boolean {
  const scene = getSceneState();
  const joints = scene?.joints || {};
  const joint = (joints as Record<string, Joint | undefined>)[jointUuid];
  if (!joint) return false;

  const actuatorList = Object.values(scene.actuators || {}) as Actuator[];
  if (actuatorsForJointDrag(jointUuid, joint.name, actuatorList, joints).length > 0) return true;

  const tendons = (scene.tendons || {}) as Record<string, Tendon | undefined>;
  return actuatorList.some((actuator) => {
    if (!isPositionLikeActuator(actuator)) return false;
    if (!actuator?.tendon && !actuator?.tendonUuid) return false;
    return tendonHasJoint(resolveTendonForActuator(actuator, tendons), jointUuid, joints);
  });
}

/**
 * While physics is running, route joint-label drags to actuator position targets + worker
 * (same keys and clamping as ActuatorControlsPanel sliders). Does not mutate scene joint `ref`.
 *
 * @returns true if at least one position-like actuator was driven
 */
export function applyJointLabelDragViaActuatorPositionControls(
  jointUuid: string,
  clampedJointTarget: number
): boolean {
  const scene = getSceneState();
  const joint = (scene?.joints as Record<string, Joint | undefined>)?.[jointUuid];
  if (!isScalarJoint(joint)) return false;

  const physics = usePhysicsStore.getState();
  const physicsWorker = getPhysicsWorker();
  if (!physics.isRunning || !physicsWorker) return false;

  const actuatorList = Object.values(scene.actuators || {}) as Actuator[];
  const matches = actuatorsForJointDrag(jointUuid, joint.name, actuatorList, scene.joints || {});
  if (matches.length === 0) return false;

  stopActiveServoSession();

  const jointsList = Object.values(scene.joints || {});
  const bodies = scene.bodies || {};
  const worldBodies = scene.worldBodies || [];
  try {
    updateGlobalActuatorMapping(actuatorList, jointsList, { bodies, worldBodies }, false);
  } catch {
    // non-fatal; payload creation may still succeed
  }

  const actuatorValues = collectActuatorPositionUiUpdatesForJoint(
    scene,
    jointUuid,
    clampedJointTarget
  );
  for (const [key, value] of Object.entries(actuatorValues)) {
    physics.actions.setActuatorPositionTarget(key, value);
  }

  if (Object.keys(actuatorValues).length === 0) return false;

  const payload = createPositionActuatorControlPayload(actuatorValues, actuatorList);
  if (Object.keys(payload).length > 0) {
    physicsWorker.postMessage({ type: "controls", payload });
  }
  return true;
}
